import { getConfig } from './config';

const STORAGE = {
  idToken: 'sentria.idToken',
  refreshToken: 'sentria.refreshToken',
  expiresAt: 'sentria.expiresAt',
  pkceVerifier: 'sentria.pkceVerifier',
  alias: 'sentria.alias',
  returnTo: 'sentria.returnTo',
} as const;

interface TokenResponse {
  id_token?: string;
  refresh_token?: string;
}

function base64UrlEncode(bytes: ArrayBuffer): string {
  let str = '';
  const view = new Uint8Array(bytes);
  for (let i = 0; i < view.length; i += 1) str += String.fromCharCode(view[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes.buffer);
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(digest);
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(escape(atob(payload))));
  } catch {
    return {};
  }
}

function aliasFromClaims(claims: Record<string, unknown>): string {
  const identities = claims.identities;
  if (Array.isArray(identities) && typeof identities[0]?.userId === 'string') {
    return identities[0].userId;
  }
  const username = claims['cognito:username'];
  if (typeof username === 'string') {
    const separator = username.indexOf('_');
    return separator >= 0 ? username.slice(separator + 1) : username;
  }
  return '';
}

export async function beginLogin(): Promise<void> {
  const config = getConfig();

  const target = window.location.pathname;
  if (target && target !== '/callback') {
    sessionStorage.setItem(STORAGE.returnTo, target);
  }

  const verifier = randomVerifier();
  sessionStorage.setItem(STORAGE.pkceVerifier, verifier);
  const challenge = await challengeFor(verifier);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.userPoolClientId,
    redirect_uri: config.redirectUri,
    scope: config.oauthScopes.join(' '),
    code_challenge_method: 'S256',
    code_challenge: challenge,
  });
  if (config.identityProvider) params.set('identity_provider', config.identityProvider);

  window.location.assign(`${config.cognitoDomain}/oauth2/authorize?${params.toString()}`);
}

export function consumeReturnTo(): string {
  const target = sessionStorage.getItem(STORAGE.returnTo);
  sessionStorage.removeItem(STORAGE.returnTo);
  return target || '/';
}

export async function exchangeCode(code: string): Promise<void> {
  const config = getConfig();
  const verifier = sessionStorage.getItem(STORAGE.pkceVerifier);
  if (!verifier) throw new Error('Missing PKCE verifier; restart sign-in.');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.userPoolClientId,
    code,
    redirect_uri: config.redirectUri,
    code_verifier: verifier,
  });

  const resp = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!resp.ok) throw new Error(`Token exchange failed (${resp.status}).`);

  persistTokens(await resp.json());
  sessionStorage.removeItem(STORAGE.pkceVerifier);
}

function persistTokens(tokens: TokenResponse): void {
  if (tokens.id_token) {
    sessionStorage.setItem(STORAGE.idToken, tokens.id_token);
    const claims = decodeJwtPayload(tokens.id_token);
    const alias = aliasFromClaims(claims);
    if (alias) sessionStorage.setItem(STORAGE.alias, alias);
    if (typeof claims.exp === 'number') sessionStorage.setItem(STORAGE.expiresAt, String(claims.exp * 1000));
  }
  if (tokens.refresh_token) sessionStorage.setItem(STORAGE.refreshToken, tokens.refresh_token);
}

async function refreshTokens(): Promise<boolean> {
  const config = getConfig();
  const refreshToken = sessionStorage.getItem(STORAGE.refreshToken);
  if (!refreshToken) return false;

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: config.userPoolClientId,
    refresh_token: refreshToken,
  });
  const resp = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!resp.ok) return false;

  persistTokens(await resp.json());
  return true;
}

let refreshPromise: Promise<boolean> | null = null;

function refreshOnce(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = refreshTokens().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function isExpired(): boolean {
  const expiresAt = Number(sessionStorage.getItem(STORAGE.expiresAt) || 0);
  // Treat the token as expired 60s early to avoid races mid-request.
  return !expiresAt || Date.now() > expiresAt - 60000;
}

export async function getIdToken(): Promise<string | null> {
  const token = sessionStorage.getItem(STORAGE.idToken);
  if (token && !isExpired()) return token;
  if (await refreshOnce()) return sessionStorage.getItem(STORAGE.idToken);
  return null;
}

export function getAlias(): string {
  return sessionStorage.getItem(STORAGE.alias) || '';
}
